import React, { useContext, useEffect } from "react";
import { observer } from "mobx-react";
import { Bolt, Check, RefreshCw } from "lucide-react";
import { AppWorkspaceContext } from "../../stores/AppWorkspace";
import {
  PAY_IN_ASSETS,
  PayInAssetCode,
  assetName,
  coinFromAssetCode,
  formatQuoteError,
  invoiceFitsRate,
  minInvoiceSatsForRate,
  payInvoiceUrl
} from "../../helpers/constants";
import { iconForSymbol } from "../../helpers/cryptoIcons";
import {
  Alert,
  Badge,
  Button,
  CopyButton,
  Field,
  PageHeader,
  Panel,
  Spinner,
  Textarea
} from "../../../ui";
import AgentDirections from "../AgentDirections";
import FindSwapBox from "../FindSwapBox";
import LandingGuide from "../LandingGuide";

const HOME_H1 = "Pay a Lightning invoice with USDC, USDT, SOL or ETH";

const NewSwapPage: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  const q = workspace.newSwap;
  const invoiceReady =
    q.invoiceSats != null && q.invoiceSats > 0 && q.parsedBolt11 === q.bolt11.trim();
  const landing = workspace.landing?.data ?? null;
  // A coin page lists its coin first.
  const preferred = landing?.assets ?? [];

  useEffect(() => {
    if (!q.bolt11.trim()) return;
    const timer = setTimeout(() => workspace.parseInvoice(), 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.bolt11]);

  useEffect(() => {
    if (!invoiceReady || !q.asset) return;
    const timer = setTimeout(() => workspace.fetchQuote(), 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.asset, q.invoiceSats, q.parsedBolt11]);

  // A single-network coin page (SOL) picks its asset once the invoice fits.
  // Multi-network pages never guess: the wrong network loses the deposit.
  useEffect(() => {
    if (!invoiceReady || q.asset || preferred.length !== 1) return;
    const rate = workspace.rates.data.find((r) => r.from === preferred[0]);
    if (rate && q.invoiceSats != null && invoiceFitsRate(q.invoiceSats, rate)) {
      q.setAsset(preferred[0] as PayInAssetCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceReady, q.parsedBolt11]);

  const createDisabled =
    !invoiceReady || !q.asset || !q.indicativeDeposit || q.quoteErrors.length > 0;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
        {landing?.h1 ?? HOME_H1}
      </h1>

      {landing?.intro ? (
        <p className="-mt-3 text-sm text-muted">{landing.intro}</p>
      ) : (
        <p className="-mt-3 text-sm text-muted">
          Instantly pay a Lightning invoice with USDC, USDT, Solana, or Ethereum. No login or signup
          necessary. Lightning Swap converts crypto to Lightning Bitcoin in a single step:
          paste a BOLT11 Lightning invoice and pay it with SOL, ETH, USDT, or USDC. We support
          stablecoin swaps on Solana, Ethereum (ERC20), and Tron (TRC20), so you can send USDT to
          Lightning, swap USDC for BTC, or turn SOL and ETH into Bitcoin on Lightning without an
          exchange account. Every quote is fixed-rate and locks in when you create the swap, with no
          order book to wait on.
        </p>
      )}

      <Panel>
        <div className="flex flex-col gap-6">
          <Field
            label="Paste BOLT11 invoice here"
            //hint="The amount is read securely on our server. Amountless, expired, and invoices with under 15 minutes left are not accepted."
          >
            <div className="relative">
              <Textarea
                placeholder="lnbc..."
                maxLength={2000}
                value={q.bolt11}
                onChange={(e) => q.setBolt11(e.currentTarget.value)}
                rows={3}
              />
              {q.parsingInvoice && (
                <div className="absolute right-3 top-3">
                  <Spinner className="h-4 w-4" />
                </div>
              )}
            </div>
          </Field>

          {invoiceReady && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Alert
                tone="success"
                title="Invoice parsed"
                icon={<Check size={18} />}
                className="min-w-0 sm:flex-1"
              >
                We will pay <strong>{q.invoiceSats!.toLocaleString()} sats</strong>.
                {q.invoiceDescription && (
                  <div className="mt-2 text-sm">
                    <span className="text-muted">Description:</span>{" "}
                    <span className="break-words">{q.invoiceDescription}</span>
                  </div>
                )}
              </Alert>
              {/* Hand the invoice to someone else to pay: the link reopens this
                  page with the invoice already in the box. */}
              <CopyButton
                value={payInvoiceUrl(q.bolt11)}
                label="Copy pay link to clipboard"
                copiedLabel="Pay link copied"
                className="shrink-0"
              />
            </div>
          )}
          {q.invoiceError && (
            <Alert tone="danger" title="Invoice not accepted">
              {q.invoiceError}
            </Alert>
          )}

          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">Pay-in asset</span>
              {!invoiceReady && <Badge tone="muted">Invoice required</Badge>}
            </div>
            <p className="mb-3 text-xs text-muted">
              Choose one of the supported assets after the invoice amount has been verified.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[...PAY_IN_ASSETS]
                .map((code, catalogIndex) => {
                  const assetRate = workspace.rates.data.find((r) => r.from === code);
                  const belowMin =
                    invoiceReady &&
                    !!assetRate?.online &&
                    q.invoiceSats != null &&
                    !invoiceFitsRate(q.invoiceSats, assetRate);
                  const offline = !assetRate?.online;
                  // Available first, then below-min by ascending floor, offline last.
                  const rank = offline ? 2 : belowMin ? 1 : 0;
                  const minSats =
                    assetRate && !offline ? minInvoiceSatsForRate(assetRate) : Number.POSITIVE_INFINITY;
                  const preferredIndex = preferred.includes(code) ? preferred.indexOf(code) : preferred.length;
                  return { code, assetRate, belowMin, offline, rank, minSats, catalogIndex, preferredIndex };
                })
                .sort(
                  (a, b) =>
                    a.rank - b.rank ||
                    a.preferredIndex - b.preferredIndex ||
                    a.minSats - b.minSats ||
                    a.catalogIndex - b.catalogIndex
                )
                .map(({ code, assetRate, belowMin, offline }) => {
                const disabled = !invoiceReady || offline || belowMin;
                const selected = q.asset === code;
                const statusLabel = offline
                  ? "Temporarily unavailable"
                  : belowMin
                    ? `Below minimum (~${minInvoiceSatsForRate(assetRate!).toLocaleString()} sats)`
                    : "Available";
                return (
                  <button
                    key={code}
                    type="button"
                    disabled={disabled}
                    aria-pressed={selected}
                    onClick={() => q.setAsset(code as PayInAssetCode)}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-3.5 text-left transition ${
                      selected
                        ? "border-accent bg-accent-soft/70"
                        : "border-border bg-paper-elevated hover:border-accent/40"
                    } ${disabled ? "cursor-not-allowed opacity-45" : "cursor-pointer"}`}
                  >
                    <img
                      src={iconForSymbol(coinFromAssetCode(code))}
                      alt=""
                      width={28}
                      height={28}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{assetName(code)}</div>
                      <div
                        className={`text-xs ${
                          !offline && !belowMin ? "text-muted" : "text-danger"
                        }`}
                      >
                        {statusLabel}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-paper px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold">Estimated deposit</span>
              {q.quoting && <Spinner className="h-4 w-4" />}
            </div>
            {q.indicativeDeposit && q.asset ? (
              <>
                <div className="font-mono text-lg font-bold">
                  {q.indicativeDeposit} {coinFromAssetCode(q.asset).toUpperCase()}
                </div>
                <p className="text-xs text-muted">
                  Indicative quote — the exact amount locks when you create the swap. Pay with{" "}
                  {assetName(q.asset)}.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted">
                {invoiceReady ? "Choose a pay-in asset to get a quote." : "Enter an invoice to begin."}
              </p>
            )}
            {q.quoteErrors.length > 0 && (
              <Alert tone="danger" title="Quote errors" className="mt-3">
                {q.quoteErrors.map(formatQuoteError).join(", ")}
              </Alert>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              leftIcon={<RefreshCw size={16} />}
              onClick={() => workspace.fetchQuote()}
              loading={q.quoting}
              disabled={!invoiceReady || !q.asset}
            >
              Refresh quote
            </Button>
            <Button
              leftIcon={<Bolt size={16} />}
              onClick={() => workspace.createSwap()}
              loading={workspace.areWeWaitingOnRails}
              disabled={createDisabled}
            >
              Create swap
            </Button>
          </div>

          <p className="text-xs text-muted">
            By initiating a swap, you confirm that you agree with our {" "}
            <a href="/terms" target="_blank" rel="noreferrer">Terms</a>.
          </p>
        </div>
      </Panel>

      {landing?.slug && <LandingGuide landing={landing} rates={workspace.rates.data} />}

      {landing && landing.links.length > 0 && (
        <nav className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label="Swap by coin">
          {landing.slug && <a href="/">All coins</a>}
          {landing.links.map((link) => (
            <a key={link.path} href={link.path}>
              {link.label} on Lightning
            </a>
          ))}
        </nav>
      )}

      {/* Without swap history, the token is the only way back to an existing
          swap — so the way back belongs on the page they land on. */}
      {!workspace.canListSwaps && <FindSwapBox compact />}

      <div className="mt-6">
        <AgentDirections />
      </div>
    </div>
  );
});

export default NewSwapPage;
